import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Circle, CheckCircle } from "@phosphor-icons/react";
import { apiClient, formatApiErrorDetail } from "../lib/api";

/**
 * TrackShare — the public page a dispatcher-forwarded "share tracking link"
 * (or a rider's own "share my ride" link) actually opens: /track/:token.
 *
 * Backend generates this exact URL shape (rider.py `create_share_link`,
 * floor_actions.py `dispatcher_share_link`) but until now there was no
 * matching frontend route — the link 404'd into the app's catch-all
 * redirect. This is the honest, read-only, minimal-PHI view the backend's
 * `GET /api/rider/track/{token}` endpoint already returns: status, first
 * names only, addresses, coarse driver position. Auto-expires (410) per
 * the same PHI policy as the rest of the sharing feature.
 */
const STATUS_LABEL = {
  REQUESTED: "awaiting a transportation provider",
  PENDING: "awaiting a transportation provider",
  OFFERED: "provider(s) considering",
  TP_ACCEPTED: "provider claimed · assigning a driver",
  ASSIGNED: "driver assigned · awaiting confirmation",
  ACCEPTED: "driver confirmed",
  EN_ROUTE: "driver en route to pickup",
  ARRIVED_PU: "driver at pickup",
  ON_TRIP: "trip in progress",
  ARRIVED_DO: "arriving at destination",
  COMPLETED: "trip completed",
  CANCELED: "trip canceled",
  CANCELED_BY_RIDER: "trip canceled",
  NO_SHOW_CONFIRMED: "no-show",
};

export default function TrackShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data: d } = await apiClient.get(`/rider/track/${token}`);
        if (alive) { setData(d); setError(null); }
      } catch (err) {
        if (!alive) return;
        const status = err?.response?.status;
        setError(
          status === 410 ? "This tracking link has expired."
          : status === 404 ? "Tracking link not found."
          : formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load this trip."
        );
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [token]);

  if (error) {
    return (
      <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center px-6" data-testid="track-share-error">
        <div className="text-center max-w-md">
          <img src="/logo.png" alt="MedexOVA" className="h-16 w-16 mx-auto logo-glow" />
          <h1 className="mt-6 font-heading text-3xl font-bold">{error}</h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-brandblue hover:text-white">
            <ArrowLeft size={14} /> Back to MedexOVA
          </Link>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center" data-testid="track-share-loading">
        <div className="text-white/60 text-sm">Loading trip…</div>
      </main>
    );
  }

  const isDone = ["COMPLETED", "CANCELED", "CANCELED_BY_RIDER", "NO_SHOW_CONFIRMED"].includes(data.status);

  return (
    <main className="min-h-screen bg-brandblue-ink text-white relative overflow-hidden" data-testid="track-share-page">
      <div aria-hidden className="orb h-[420px] w-[420px] bg-brandblue/30 -top-24 -left-20" />
      <div aria-hidden className="absolute inset-0 grid-bg opacity-50" />

      <header className="relative z-10">
        <div className="max-w-2xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 group">
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-md bg-transparent overflow-hidden">
              <img src="/logo.png" alt="MedexOVA" className="h-8 w-8 object-contain logo-glow" />
            </span>
            <span className="font-heading text-base tracking-tight font-black">
              <span className="text-[#16A34A]">Medex</span><span className="text-[#C4B5FD]">OVA</span>
            </span>
          </Link>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-white/60">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brandblue"></span>
            </span>
            Live tracking
          </span>
        </div>
      </header>

      <section className="relative z-10 max-w-2xl mx-auto px-6 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-black leading-tight" data-testid="track-share-status">
            {STATUS_LABEL[data.status] || data.status}
            {isDone && data.status === "COMPLETED" && <CheckCircle size={32} weight="fill" className="inline ml-3 text-forest" />}
          </h1>
          {data.rider_first && (
            <p className="mt-2 text-white/60 text-sm">
              {data.rider_first}{data.driver_first ? ` · driver ${data.driver_first}` : ""}
            </p>
          )}
        </motion.div>

        {!isDone && (
          <div className="mt-8 space-y-3">
            {data.pickup_address && (
              <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 flex items-start gap-3">
                <Circle weight="fill" size={12} className="mt-1 text-forest" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/50">Pickup</div>
                  <div className="text-sm text-white">{data.pickup_address}</div>
                </div>
              </div>
            )}
            {data.dropoff_address && (
              <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 flex items-start gap-3">
                <MapPin weight="fill" size={12} className="mt-1 text-terracotta" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/50">Drop-off</div>
                  <div className="text-sm text-white">{data.dropoff_address}</div>
                </div>
              </div>
            )}
            {data.driver_position && (
              <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 text-sm text-white/70" data-testid="track-share-driver-position">
                Driver's coarse position is updating live · refreshes every 20s
              </div>
            )}
          </div>
        )}

        {isDone && (
          <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 text-sm text-white/70">
            This trip has ended. This link will stop working once it expires.
          </div>
        )}

        <div className="mt-10 text-[11px] text-white/40">
          Read-only view · no personal details beyond first names · auto-refreshes every 20s
        </div>
      </section>
    </main>
  );
}
